<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import GroupParticipantName from '../GroupParticipantName.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const instagramEmbedUrl = computed(() => {
  if (!isReel.value) return '';

  try {
    const url = new URL(attachment.value.dataUrl);
    if (!url.hostname.endsWith('instagram.com')) return '';

    const match = url.pathname.match(/^\/(?:reel|reels|p|tv)\/([^/]+)/);
    return match ? `https://www.instagram.com/reel/${match[1]}/embed/` : '';
  } catch {
    return '';
  }
});
</script>

<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="video">
    <GroupParticipantName />
    <div class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <iframe
        v-if="instagramEmbedUrl"
        :src="instagramEmbedUrl"
        class="h-[480px] w-[270px] max-w-full rounded-lg border-0 bg-n-slate-1"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
        loading="lazy"
      />
      <video
        v-else
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        playsinline
        preload="metadata"
        @click.stop
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
